import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const FIELDS = [
  { name: "name", label: "会社名" },
  { name: "address", label: "住所" },
  { name: "phone", label: "電話番号" },
  { name: "email", label: "メール" },
];
const LANG_OPTS = [
  { value: "ja", label: "日本語" },
  { value: "en", label: "English" },
  { value: "pt", label: "Português" },
];

export default function SettingsCompany() {
  const { hasWrite } = useAuth();
  const { t } = useI18n();
  const canWrite = hasWrite("company");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setData(data || {})).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/company", data); toast.success("保存しました"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "保存に失敗しました"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="animate-fade-in max-w-2xl">
      <PageHeader title="会社設定" subtitle="自社の基本情報" />
      <div className="bg-white border border-border rounded-sm p-6 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.name}>
            <Label className="text-xs text-muted-foreground mb-1.5 block">{t(f.label)}</Label>
            <Input value={data[f.name] || ""} onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
              disabled={!canWrite} data-testid={`company-${f.name}`} />
          </div>
        ))}
        <div className="pt-2 border-t border-border">
          <div className="text-sm font-medium text-[#0A0A0A] mb-1">{t("宅地建物取引業者情報")}</div>
          <p className="text-xs text-muted-foreground mb-3">{t("重要事項説明書・契約書に記載されます。")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("免許番号")}</Label>
              <Input value={data.license_number || ""} onChange={(e) => setData({ ...data, license_number: e.target.value })} disabled={!canWrite} placeholder="東京都知事(1)第00000号" data-testid="company-license_number" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("代表者名")}</Label>
              <Input value={data.representative_name || ""} onChange={(e) => setData({ ...data, representative_name: e.target.value })} disabled={!canWrite} data-testid="company-representative_name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("宅地建物取引士 氏名")}</Label>
              <Input value={data.agent_name || ""} onChange={(e) => setData({ ...data, agent_name: e.target.value })} disabled={!canWrite} data-testid="company-agent_name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("宅建士 登録番号")}</Label>
              <Input value={data.agent_reg_number || ""} onChange={(e) => setData({ ...data, agent_reg_number: e.target.value })} disabled={!canWrite} placeholder="東京都 第00000号" data-testid="company-agent_reg_number" />
            </div>
          </div>
        </div>
        <div className="pt-2 border-t border-border">
          <div className="text-sm font-medium text-[#0A0A0A] mb-1">{t("支払方法・振込先")}</div>
          <p className="text-xs text-muted-foreground mb-3">{t("入力した内容は全契約の重要事項・契約書PDFに表示されます。")}</p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("支払方法")}</Label>
              <select value={data.pay_method || ""} onChange={(e) => setData({ ...data, pay_method: e.target.value })}
                disabled={!canWrite} className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="company-pay_method">
                <option value="">{t("選択してください")}</option>
                {["銀行振込", "口座振替", "その他"].map((o) => <option key={o} value={o}>{t(o)}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("銀行名")}</Label>
                <Input value={data.bank_name || ""} onChange={(e) => setData({ ...data, bank_name: e.target.value })} disabled={!canWrite} placeholder="三井住友銀行" data-testid="company-bank_name" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("支店名")}</Label>
                <Input value={data.branch_name || ""} onChange={(e) => setData({ ...data, branch_name: e.target.value })} disabled={!canWrite} placeholder="品川支店" data-testid="company-branch_name" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("預金種別")}</Label>
                <select value={data.bank_account_type || ""} onChange={(e) => setData({ ...data, bank_account_type: e.target.value })}
                  disabled={!canWrite} className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="company-bank_account_type">
                  <option value="">{t("選択してください")}</option>
                  {["普通", "当座"].map((o) => <option key={o} value={o}>{t(o)}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("口座番号")}</Label>
                <Input value={data.account_number || ""} onChange={(e) => setData({ ...data, account_number: e.target.value })} disabled={!canWrite} placeholder="1234567" data-testid="company-account_number" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("口座名義")}</Label>
                <Input value={data.account_holder || ""} onChange={(e) => setData({ ...data, account_holder: e.target.value })} disabled={!canWrite} placeholder="ネオ不動産管理（カ" data-testid="company-account_holder" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("支払期日")}</Label>
                <Input value={data.pay_due || ""} onChange={(e) => setData({ ...data, pay_due: e.target.value })} disabled={!canWrite} placeholder="毎月末日" data-testid="company-pay_due" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t("遅延損害金率")}（%）</Label>
                <Input type="number" step="0.1" value={data.late_fee_rate ?? ""} onChange={(e) => setData({ ...data, late_fee_rate: e.target.value })} disabled={!canWrite} placeholder="14.6" data-testid="company-late_fee_rate" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("補足事項")}（{t("任意")}）</Label>
              <Textarea value={data.payment_info || ""} onChange={(e) => setData({ ...data, payment_info: e.target.value })}
                disabled={!canWrite} rows={2} data-testid="company-payment_info" />
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">{t("言語")}（{t("メール通知")}）</Label>
          <select value={data.language || "ja"} onChange={(e) => setData({ ...data, language: e.target.value })}
            disabled={!canWrite}
            className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="company-language">
            {LANG_OPTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        {canWrite && (
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="company-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("保存")}
          </Button>
        )}
      </div>
    </div>
  );
}
